import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { hash } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { guidFor } from '@ember/object/internals';
import { PhX } from 'ember-phosphor-icons';
import type { PopoverIntent } from '../color-tokens.ts';
import type { TOC } from '@ember/component/template-only';
import {
  computePosition,
  installOutsideClickHandler,
  installEscapeHandler,
  installScrollHandler,
  installResizeObserver,
  installWindowResizeHandler,
} from '../utils/floating.ts';
import type { FloatingPlacement, FloatingPosition, ResolvedSide } from '../utils/floating.ts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Re-export FloatingPlacement as PopoverPlacement for public API */
export type PopoverPlacement = FloatingPlacement;

type PopoverTriggerMode = 'click' | 'hover';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Hover close delay in ms (prevents accidental close when moving to popover) */
const HOVER_DELAY = 150;

/** Default max-width */
const DEFAULT_MAX_WIDTH = 360;

// ---------------------------------------------------------------------------
// Sub-components: Header, Body, Footer
// ---------------------------------------------------------------------------

interface PopoverHeaderSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const PopoverHeader: TOC<PopoverHeaderSignature> = <template>
  <div
    data-test-popover-header
    class="app-popover-section app-popover-section--bordered-bottom text-sm font-semibold text-[var(--text-primary)]"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

interface PopoverBodySignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const PopoverBody: TOC<PopoverBodySignature> = <template>
  <div
    data-test-popover-body
    class="flex-1 px-4 py-3 text-sm text-[var(--text-secondary)]"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

interface PopoverFooterSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const PopoverFooter: TOC<PopoverFooterSignature> = <template>
  <div
    data-test-popover-footer
    class="app-popover-section app-popover-section--bordered-top"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

// ---------------------------------------------------------------------------
// Main Popover component
// ---------------------------------------------------------------------------

/**
 * Reimagined Popover — a space-aware, animated, premium popover overlay.
 *
 * **Features:**
 * - Smart positioning: preferred placement with automatic viewport-aware flip.
 *   Even if you specify `right`, it will flip to `left` if there is no room.
 * - Smooth spring-feel entrance animation (scale + opacity + translate)
 * - Intent-based color theming (default, info, success, warning, danger)
 * - Optional colored accent bar on the edge facing the trigger
 * - Click or hover trigger modes with smart hover delay
 * - Escape key, outside-click, and scroll-close dismiss
 * - Named blocks: header, body, footer — or a freeform default block
 * - Portals to `document.body` to escape stacking contexts
 * - Resize observer to reposition when content changes
 * - Full dark mode support
 *
 * @example Basic click popover
 * ```gts
 * import Popover from 'tio-reimagined/components/popover';
 *
 * <Popover @placement="bottom">
 *   <:trigger as |t|>
 *     <button {{t.register}} {{on "click" t.toggle}}>
 *       More info
 *     </button>
 *   </:trigger>
 *   <:body>
 *     <p>This is some helpful context.</p>
 *   </:body>
 * </Popover>
 * ```
 *
 * @example Hover tooltip with intent
 * ```gts
 * <Popover @placement="top" @triggerMode="hover" @intent="info">
 *   <:trigger as |t|>
 *     <span {{t.register}} {{t.hoverHandlers}}>Hover me</span>
 *   </:trigger>
 *   <:body>
 *     <p>Quick info tooltip</p>
 *   </:body>
 * </Popover>
 * ```
 *
 * @example Rich popover with all blocks
 * ```gts
 * <Popover @placement="right" @intent="success" @showAccentBar={{true}} @maxWidth={{420}}>
 *   <:trigger as |t|>
 *     <Button {{t.register}} {{on "click" t.toggle}}>Details</Button>
 *   </:trigger>
 *   <:header>
 *     <div class="tio-row-tight">
 *       <PhCheckCircle @size={{18}} class="text-emerald-500" />
 *       <span>Application Approved</span>
 *     </div>
 *   </:header>
 *   <:body>
 *     <p>Your tuition assistance has been approved for $5,250.</p>
 *   </:body>
 *   <:footer>
 *     <div class="tio-flex-end">
 *       <Button @intent="primary" @size="sm">View Details</Button>
 *     </div>
 *   </:footer>
 * </Popover>
 * ```
 *
 * @example Controlled open state
 * ```gts
 * <Popover
 *   @placement="bottom"
 *   @isOpen={{this.showPopover}}
 *   @onClose={{this.hidePopover}}
 *   @onOpen={{this.showPopoverAction}}
 * >
 *   <:trigger as |t|>
 *     <button {{t.register}} {{on "click" t.toggle}}>Toggle</button>
 *   </:trigger>
 *   <:body>Controlled content</:body>
 * </Popover>
 * ```
 */
interface PopoverSignature {
  Args: {
    /** Preferred placement relative to trigger @default "bottom" */
    placement?: PopoverPlacement;
    /** Trigger mode — 'click' opens on click, 'hover' opens on mouseenter @default "click" */
    triggerMode?: PopoverTriggerMode;
    /** Color intent for theming @default "default" */
    intent?: PopoverIntent;
    /** Show the colored accent bar on the edge nearest the trigger @default false */
    showAccentBar?: boolean;
    /** Show a close button in the top-right corner @default false */
    showCloseButton?: boolean;
    /** Max-width of the popover in px @default 360 */
    maxWidth?: number;
    /** Whether the popover is forced open (controlled mode) */
    isOpen?: boolean;
    /** Called when the popover requests to close */
    onClose?: () => void;
    /** Called when the popover opens */
    onOpen?: () => void;
    /** Close on Escape key @default true */
    closeOnEscape?: boolean;
    /** Close when clicking outside @default true */
    closeOnOutsideClick?: boolean;
    /** Close when an ancestor scrolls @default true */
    closeOnScroll?: boolean;
    /** Keep popover anchored to trigger on scroll instead of closing @default true */
    sticky?: boolean;
    /** Accessible label for the popover panel */
    ariaLabel?: string;
  };
  Blocks: {
    /** Trigger block — yields `register` modifier, `hoverHandlers` modifier, toggle/open/close actions, and ARIA attributes */
    trigger: [
      {
        register: Popover['registerTrigger'];
        toggle: () => void;
        open: () => void;
        close: () => void;
        hoverHandlers: Popover['hoverHandlers'];
        isOpen: boolean;
        panelId: string;
      },
    ];
    /** Optional header block */
    header?: [];
    /** Body content */
    body?: [];
    /** Optional footer block */
    footer?: [];
    /** Default (freeform) block — used when body is not provided */
    default?: [];
  };
  Element: HTMLDivElement;
}

export default class Popover extends Component<PopoverSignature> {
  // ── State ─────────────────────────────────────────────────────────

  @tracked private _isOpen = false;
  @tracked private _visible = false;
  @tracked private _triggerRect: DOMRect | null = null;
  @tracked private _position: FloatingPosition | null = null;

  private _panelId = `reimagined-popover-${guidFor(this)}`;
  private _triggerId = `reimagined-popover-trigger-${guidFor(this)}`;

  private _triggerElement: HTMLElement | null = null;
  private _popoverElement: HTMLElement | null = null;
  private _hoverCloseTimer: ReturnType<typeof setTimeout> | undefined;
  private _closeTimer: ReturnType<typeof setTimeout> | undefined;

  // ── Getters ───────────────────────────────────────────────────────

  private get isControlled() {
    return this.args.isOpen !== undefined;
  }

  get isOpen() {
    if (this.isControlled) {
      return this.args.isOpen ?? false;
    }

    return this._isOpen;
  }

  private get placement(): PopoverPlacement {
    return this.args.placement ?? 'bottom';
  }

  private get triggerMode(): PopoverTriggerMode {
    return this.args.triggerMode ?? 'click';
  }

  private get intent(): PopoverIntent {
    return this.args.intent ?? 'default';
  }

  /**
   * Single modifier class for the popover surface — composes onto the
   * .app-popover base. `default` intent uses just the base class (no
   * modifier needed); status intents add `.app-popover--<intent>` which
   * sets --popover-border / --popover-accent.
   */
  get intentClass() {
    return this.intent === 'default' ? '' : `app-popover--${this.intent}`;
  }

  private get showAccentBar() {
    return this.args.showAccentBar ?? false;
  }

  private get showCloseButton() {
    return this.args.showCloseButton ?? false;
  }

  private get maxWidth() {
    return this.args.maxWidth ?? DEFAULT_MAX_WIDTH;
  }

  private get closeOnEscape() {
    return this.args.closeOnEscape ?? true;
  }

  private get closeOnOutsideClick() {
    return this.args.closeOnOutsideClick ?? true;
  }

  private get closeOnScroll() {
    return this.args.closeOnScroll ?? true;
  }

  private get sticky() {
    return this.args.sticky ?? true;
  }

  private get panelRole() {
    return this.triggerMode === 'hover' ? 'tooltip' : 'dialog';
  }

  private get portalDestination(): Element {
    return document.body;
  }

  // ── Computed styles ───────────────────────────────────────────────

  private get popoverStyle() {
    if (!this._position) {
      // Use visibility:hidden + offscreen position so the browser can measure
      // the popover dimensions on mount before animating it in.
      return htmlSafe(
        `position:fixed;top:-9999px;left:-9999px;max-width:${this.maxWidth}px;visibility:hidden;pointer-events:none;z-index:90;`
      );
    }
    const p = this._position;
    return htmlSafe(
      `position:fixed;top:${p.top}px;left:${p.left}px;max-width:${this.maxWidth}px;transform-origin:${p.transformOrigin};z-index:90;`
    );
  }

  private get accentBarClasses(): string {
    if (!this._position) return '';
    const side = this._position.resolvedSide;
    // Accent bar goes on the side FACING the trigger (opposite of resolved side)
    const barMap: Record<ResolvedSide, string> = {
      top: 'absolute bottom-0 left-0 right-0 h-0.5 rounded-b-xl',
      bottom: 'absolute top-0 left-0 right-0 h-0.5 rounded-t-xl',
      left: 'absolute top-0 bottom-0 right-0 w-0.5 rounded-r-xl',
      right: 'absolute top-0 bottom-0 left-0 w-0.5 rounded-l-xl',
    };
    return barMap[side];
  }

  private get panelTransitionClasses(): string {
    return this._visible
      ? 'opacity-100 scale-100 translate-y-0'
      : 'opacity-0 scale-95 translate-y-1';
  }

  // ── Actions ───────────────────────────────────────────────────────

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open = () => {
    if (this.isOpen) return;
    this.snapshotTrigger();
    if (this.isControlled) {
      this.args.onOpen?.();
    } else {
      this._isOpen = true;
      this.args.onOpen?.();
    }
  };

  close = () => {
    if (!this.isOpen) return;
    this._visible = false;
    if (this._hoverCloseTimer) clearTimeout(this._hoverCloseTimer);

    // Let exit animation play before unmounting
    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      if (this.isControlled) {
        this.args.onClose?.();
      } else {
        this._isOpen = false;
        this.args.onClose?.();
      }
      this._position = null;
      this._closeTimer = undefined;
    }, 200);
  };

  handleCloseButton = () => {
    this.close();
  };

  private snapshotTrigger = () => {
    if (this._triggerElement) {
      this._triggerRect = this._triggerElement.getBoundingClientRect();
    }
  };

  private reposition = () => {
    if (!this._popoverElement || !this._triggerElement) return;
    this._triggerRect = this._triggerElement.getBoundingClientRect();
    this._position = computePosition(
      this._triggerRect,
      this._popoverElement,
      this.placement,
      this.maxWidth
    );
  };

  // ── Modifiers ─────────────────────────────────────────────────────

  /**
   * Register the trigger element. Yielded to the trigger block.
   */
  registerTrigger = modifier((element: HTMLElement) => {
    this._triggerElement = element;
    // Ensure trigger has an ID for floating ownership tracking
    if (!element.id) {
      element.id = this._triggerId;
    }
    return () => {
      this._triggerElement = null;
    };
  });

  /**
   * Hover handlers modifier for hover trigger mode.
   * Applies mouseenter/mouseleave on the trigger element.
   * Also handles focusin/focusout for keyboard accessibility.
   */
  hoverHandlers = modifier((element: HTMLElement) => {
    if (this.triggerMode !== 'hover') return;

    const enter = () => {
      if (this._hoverCloseTimer) {
        clearTimeout(this._hoverCloseTimer);
        this._hoverCloseTimer = undefined;
      }
      this.open();
    };

    const leave = () => {
      this._hoverCloseTimer = setTimeout(() => this.close(), HOVER_DELAY);
    };

    const focusOut = (e: FocusEvent) => {
      // Don't close if focus moves to the popover panel
      const related = e.relatedTarget as Node | null;
      if (related && this._popoverElement?.contains(related)) return;
      leave();
    };

    element.addEventListener('mouseenter', enter);
    element.addEventListener('mouseleave', leave);
    element.addEventListener('focusin', enter);
    element.addEventListener('focusout', focusOut);

    return () => {
      element.removeEventListener('mouseenter', enter);
      element.removeEventListener('mouseleave', leave);
      element.removeEventListener('focusin', enter);
      element.removeEventListener('focusout', focusOut);
    };
  });

  /**
   * Installs the popover panel: computes position, attaches dismiss handlers
   * (outside-click, escape, scroll, resize), and hover listeners.
   */
  registerPopover = modifier((element: HTMLElement) => {
    this._popoverElement = element;
    const cleanups: (() => void)[] = [];

    // Initial position calc (two rAFs: one to measure, one to animate in)
    requestAnimationFrame(() => {
      this.reposition();
      requestAnimationFrame(() => {
        this._visible = true;
      });
    });

    // ── Dismiss handlers (shared utilities) ─────────────────────────

    if (this.closeOnOutsideClick) {
      cleanups.push(
        installOutsideClickHandler([this._triggerElement, element], () => this.close())
      );
    }

    if (this.closeOnEscape) {
      cleanups.push(installEscapeHandler(() => this.close(), this._triggerElement));
    }

    if (this.sticky) {
      cleanups.push(
        installScrollHandler({
          floatingEl: element,
          mode: 'reposition',
          onReposition: () => this.reposition(),
          onClose: () => this.close(),
          triggerEl: this._triggerElement,
        })
      );
    } else if (this.closeOnScroll) {
      cleanups.push(
        installScrollHandler({
          floatingEl: element,
          mode: 'close',
          onClose: () => this.close(),
          debounceMs: 100,
        })
      );
    }

    cleanups.push(installResizeObserver(element, () => this.reposition()));
    cleanups.push(installWindowResizeHandler(() => this.reposition()));

    // ── Hover on popover panel (for hover mode) ────────────────────
    const panelEnter = () => {
      if (this.triggerMode !== 'hover') return;
      if (this._hoverCloseTimer) {
        clearTimeout(this._hoverCloseTimer);
        this._hoverCloseTimer = undefined;
      }
    };

    const panelLeave = () => {
      if (this.triggerMode !== 'hover') return;
      this._hoverCloseTimer = setTimeout(() => this.close(), HOVER_DELAY);
    };

    // ── Focus handling on popover panel (for hover mode) ────────────
    const panelFocusIn = () => {
      if (this.triggerMode !== 'hover') return;
      if (this._hoverCloseTimer) {
        clearTimeout(this._hoverCloseTimer);
        this._hoverCloseTimer = undefined;
      }
    };

    const panelFocusOut = (e: FocusEvent) => {
      if (this.triggerMode !== 'hover') return;
      const related = e.relatedTarget as Node | null;
      // Don't close if focus moves back to the trigger or stays in the panel
      if (related && (element.contains(related) || this._triggerElement?.contains(related))) return;
      this._hoverCloseTimer = setTimeout(() => this.close(), HOVER_DELAY);
    };

    element.addEventListener('mouseenter', panelEnter);
    element.addEventListener('mouseleave', panelLeave);
    element.addEventListener('focusin', panelFocusIn);
    element.addEventListener('focusout', panelFocusOut);

    return () => {
      for (const cleanup of cleanups) cleanup();
      element.removeEventListener('mouseenter', panelEnter);
      element.removeEventListener('mouseleave', panelLeave);
      element.removeEventListener('focusin', panelFocusIn);
      element.removeEventListener('focusout', panelFocusOut);
      if (this._hoverCloseTimer) clearTimeout(this._hoverCloseTimer);
      if (this._closeTimer) clearTimeout(this._closeTimer);
      this._popoverElement = null;
    };
  });

  // ── Lifecycle anchor ──────────────────────────────────────────────

  manageOpen = modifier((_element: HTMLElement, [isOpen]: [boolean]) => {
    if (isOpen) {
      this.snapshotTrigger();
    } else {
      this._visible = false;
      this._position = null;
    }
  });

  // ── Template ──────────────────────────────────────────────────────

  <template>
    {{! Lifecycle anchor — invisible element that hooks into open/close state }}
    <div
      class="reimagined-popover-anchor contents"
      {{this.manageOpen this.isOpen}}
      aria-hidden="true"
    ></div>

    {{! Trigger block — yields modifiers and actions }}
    {{yield
      (hash
        register=this.registerTrigger
        toggle=this.toggle
        open=this.open
        close=this.close
        hoverHandlers=this.hoverHandlers
        isOpen=this.isOpen
        panelId=this._panelId
      )
      to="trigger"
    }}

    {{! Popover panel — portaled to document.body }}
    {{#if this.isOpen}}
      {{#in-element this.portalDestination insertBefore=null}}
        <div
          data-test-popover
          data-test-popover-intent={{this.intent}}
          data-floating-trigger-id={{if
            this._triggerElement
            this._triggerElement.id
            this._triggerId
          }}
          id={{this._panelId}}
          class="reimagined-popover"
          style={{this.popoverStyle}}
          role={{this.panelRole}}
          aria-label={{@ariaLabel}}
          {{this.registerPopover}}
          ...attributes
        >
          {{! Panel container with animation. .app-popover handles bg /
              border / shadow / radius via canonical tokens; the intent
              modifier (info / success / warning / danger) adds the
              status accent + matching border color. }}
          <div
            data-test-popover-panel
            class="app-popover
              {{this.intentClass}}
              backdrop-blur-sm relative transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]
              {{this.panelTransitionClasses}}"
          >
            {{! Accent bar — colored edge strip facing the trigger }}
            {{#if this.showAccentBar}}
              <div
                data-test-popover-accent
                class="app-popover-accent-bar {{this.accentBarClasses}}"
              ></div>
            {{/if}}

            {{! Close button }}
            {{#if this.showCloseButton}}
              <button
                data-test-popover-close
                type="button"
                class="absolute top-2 right-2 z-10 p-1 rounded-md text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-sunken)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
                {{! template-lint-disable no-bare-strings }}
                aria-label="Close popover"
                {{on "click" this.handleCloseButton}}
              >
                <PhX @size={{16}} />
              </button>
            {{/if}}

            {{! Named blocks }}
            {{#if (has-block "header")}}
              <PopoverHeader>
                {{yield to="header"}}
              </PopoverHeader>
            {{/if}}

            {{#if (has-block "body")}}
              <PopoverBody>{{yield to="body"}}</PopoverBody>
            {{else if (has-block)}}
              <PopoverBody>{{yield}}</PopoverBody>
            {{/if}}

            {{#if (has-block "footer")}}
              <PopoverFooter>{{yield to="footer"}}</PopoverFooter>
            {{/if}}

          </div>

        </div>
      {{/in-element}}
    {{/if}}
  </template>
}

export type { PopoverSignature, PopoverIntent };
